/* Controlled Chaos — Size Guide logic

   SIZE LABELS ARE NEVER INVENTED. The sizes shown always come from either:
     1. the product's custom.size_chart_data metafield (real garment measurements), or
     2. the product's actual Size variant options (labels only, no measurements).
   There is no hardcoded S–XXL list — if a product only sells S–XL, only S–XL appear.

   MEASUREMENT VALUES may be either a single number (68.5) or a two-number range
   ([71, 76] — rendered "71–76"), used for waist and body-chest measurements.
   Both forms convert correctly with the CM/IN toggle.

   Recommendation = body match (height/weight) blended with the customer's USUAL SIZE,
   then adjusted by their PREFERRED FIT (Fitted / True to size / Oversized).
   CC garments are cut OVERSIZED, so:
   - ties resolve to the SMALLER size;
   - Oversized preference steps UP one size;
   - Fitted steps DOWN one size only when the smaller size can still physically fit
     (never scales up). Example: 178 cm / 74 kg, usually S, Fitted -> S; Oversized -> M.
*/
(function () {
  'use strict';

  /* Estimated fit ranges per standard label, used ONLY for sizes the product
     actually sells and ONLY when that product has no metafield chart yet. */
  var FALLBACK_FIT = {
    'XS':  { weight: [40, 54],   height: [148, 164] },
    'S':   { weight: [50, 75],   height: [155, 179] },
    'M':   { weight: [76, 88],   height: [170, 184] },
    'L':   { weight: [89, 100],  height: [176, 190] },
    'XL':  { weight: [101, 113], height: [182, 196] },
    'XXL': { weight: [114, 128], height: [186, 202] },
    '2XL': { weight: [114, 128], height: [186, 202] },
    'XXXL':{ weight: [126, 142], height: [188, 204] },
    '3XL': { weight: [126, 142], height: [188, 204] }
  };

  function normLabel(l) {
    return String(l || '').toUpperCase().replace(/\s+/g, '');
  }

  function parseJSONFrom(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    var out = null;
    try { out = JSON.parse(el.textContent); } catch (e) { out = null; }
    if (typeof out === 'string') {
      try { out = JSON.parse(out); } catch (e) { out = null; }
    }
    return out;
  }

  function parseOptionLabels(root) {
    var raw = parseJSONFrom(root, '[data-cc-sc-options]');
    if (!Array.isArray(raw)) return [];
    return raw.map(function (v) {
      return (v && typeof v === 'object') ? String(v.name || '') : String(v || '');
    }).filter(function (v) { return v !== ''; });
  }

  function buildFromOptions(labels) {
    return {
      unit: 'cm',
      columns: [],
      estimated: true,
      sizes: labels.map(function (l) {
        var s = { label: l };
        var fb = FALLBACK_FIT[normLabel(l)];
        if (fb) { s.weight = fb.weight; s.height = fb.height; }
        return s;
      })
    };
  }

  function parseData(root) {
    var data = parseJSONFrom(root, '[data-cc-sc-data]');
    var labels = parseOptionLabels(root);

    if (data && Array.isArray(data.sizes) && data.sizes.length) {
      if (!Array.isArray(data.columns)) data.columns = [];
      if (data.unit !== 'in') data.unit = 'cm';
      data.estimated = false;
      return data;
    }
    return buildFromOptions(labels);
  }

  function rangeFor(size, key) {
    if (Array.isArray(size[key]) && size[key].length === 2 && isFinite(size[key][0]) && isFinite(size[key][1])) {
      return size[key];
    }
    var fb = FALLBACK_FIT[normLabel(size.label)];
    return fb ? fb[key] : null;
  }

  function distance(v, range) {
    if (!range) return null;
    if (v < range[0]) return range[0] - v;
    if (v > range[1]) return v - range[1];
    return 0;
  }

  function recommendBody(data, height, weight) {
    var best = null;
    data.sizes.forEach(function (s, i) {
      var dw = distance(weight, rangeFor(s, 'weight'));
      var dh = distance(height, rangeFor(s, 'height'));
      if (dw === null && dh === null) return;
      var score = (dw === null ? 0 : dw * 1.2) + (dh === null ? 0 : dh * 0.8);
      if (!best || score < best.score) {
        best = { size: s, score: score, index: i };
      }
    });
    return best;
  }

  function canWear(size, h, w) {
    var wr = rangeFor(size, 'weight');
    var hr = rangeFor(size, 'height');
    var okW = wr ? (w === null || w <= wr[1] + 6) : true;
    var okH = hr ? (h === null || h <= hr[1] + 6) : true;
    return okW && okH;
  }

  function hasFitData(data) {
    return data.sizes.some(function (s) {
      return rangeFor(s, 'weight') || rangeFor(s, 'height');
    });
  }

  function fmtNum(v) {
    var r = Math.round(v * 10) / 10;
    return (r % 1 === 0) ? String(Math.round(r)) : r.toFixed(1);
  }

  function convert(v, from, to) {
    if (typeof v !== 'number' || !isFinite(v)) return v;
    if (from === to) return fmtNum(v);
    return to === 'in' ? fmtNum(v / 2.54) : fmtNum(v * 2.54);
  }

  /* Renders a measurement cell: single number, [min,max] range, or an em-dash. */
  function formatMeasure(v, from, to) {
    if (v === undefined || v === null || v === '') return '\u2014';
    if (Array.isArray(v)) {
      if (v.length !== 2 || !isFinite(Number(v[0])) || !isFinite(Number(v[1]))) return '\u2014';
      return convert(Number(v[0]), from, to) + '\u2013' + convert(Number(v[1]), from, to);
    }
    var n = Number(v);
    if (!isFinite(n)) return String(v);
    return convert(n, from, to);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderTable(root, data, unit, recommendedLabel) {
    var wrap = root.querySelector('[data-cc-sc-table]');
    if (!wrap) return;

    if (!data.columns.length) {
      var list = data.sizes.map(function (s) {
        var isRec = recommendedLabel && String(s.label) === String(recommendedLabel);
        return '<li' + (isRec ? ' class="is-recommended"' : '') + '>' + escapeHtml(s.label || '') + '</li>';
      }).join('');
      wrap.innerHTML =
        '<div class="cc-sizechart__empty">' +
        '<p class="cc-sizechart__empty-title">Available sizes</p>' +
        '<ul class="cc-sizechart__sizelist">' + list + '</ul>' +
        '<p class="cc-sizechart__empty-note">Exact garment measurements for this piece are being added. In the meantime, Find My Size will still point you to the right fit.</p>' +
        '</div>';
      return;
    }

    var cols = data.columns;
    var html = '<table class="cc-sizechart__table"><thead><tr><th>Size</th>';
    cols.forEach(function (c) {
      html += '<th>' + escapeHtml(c) + ' (' + unit + ')</th>';
    });
    html += '</tr></thead><tbody>';
    data.sizes.forEach(function (s) {
      var isRec = recommendedLabel && String(s.label) === String(recommendedLabel);
      html += '<tr' + (isRec ? ' class="is-recommended"' : '') + '><td>' + escapeHtml(s.label || '') + '</td>';
      var m = Array.isArray(s.measurements) ? s.measurements : [];
      cols.forEach(function (c, i) {
        html += '<td>' + escapeHtml(formatMeasure(m[i], data.unit, unit)) + '</td>';
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    wrap.innerHTML = html;
  }

  function renderUsualPills(root, data) {
    var wrap = root.querySelector('[data-cc-sc-usual]');
    if (!wrap) return;
    var html = '';
    data.sizes.forEach(function (s, i) {
      html += '<button type="button" data-cc-sc-usual-opt="' + i + '" aria-pressed="false">' + escapeHtml(s.label || '') + '</button>';
    });
    wrap.innerHTML = html;
    wrap.querySelectorAll('button').forEach(function (b) {
      b.addEventListener('click', function () {
        var wasActive = b.classList.contains('is-active');
        wrap.querySelectorAll('button').forEach(function (x) {
          x.classList.remove('is-active');
          x.setAttribute('aria-pressed', 'false');
        });
        if (!wasActive) {
          b.classList.add('is-active');
          b.setAttribute('aria-pressed', 'true');
        }
      });
    });
  }

  function getUsualIndex(root) {
    var el = root.querySelector('[data-cc-sc-usual] button.is-active');
    if (!el) return null;
    var i = parseInt(el.getAttribute('data-cc-sc-usual-opt'), 10);
    return isFinite(i) ? i : null;
  }

  function init(root) {
    if (root.__ccScInit) return;
    root.__ccScInit = true;

    var data = parseData(root);
    if (!data.sizes.length) return;

    var panel = root.querySelector('.cc-sizechart__panel');
    var openBtn = root.querySelector('[data-cc-sc-open]');
    var resultEl = root.querySelector('[data-cc-sc-result]');
    var fitEl = root.querySelector('[data-cc-sc-fit]');
    var fitLabelsEl = root.querySelector('[data-cc-sc-fit-labels]');
    var unitsEl = root.querySelector('[data-cc-sc-units]');
    var currentUnit = data.unit;
    var recommendedLabel = null;
    var fitDataAvailable = hasFitData(data);

    if (!data.columns.length && unitsEl) unitsEl.style.display = 'none';

    renderTable(root, data, currentUnit, null);
    renderUsualPills(root, data);

    function syncFitLabels() {
      if (!fitEl || !fitLabelsEl) return;
      var v = parseInt(fitEl.value, 10) || 0;
      var spans = fitLabelsEl.querySelectorAll('span');
      spans.forEach(function (sp, i) { sp.classList.toggle('is-active', i === v); });
    }
    if (fitEl) {
      fitEl.addEventListener('input', syncFitLabels);
      fitEl.addEventListener('change', syncFitLabels);
      syncFitLabels();
    }

    function open() {
      root.classList.add('is-open');
      document.body.classList.add('cc-sc-lock');
      if (panel) panel.focus({ preventScroll: true });
      document.addEventListener('keydown', onKey);
    }

    function close() {
      root.classList.remove('is-open');
      document.body.classList.remove('cc-sc-lock');
      document.removeEventListener('keydown', onKey);
      if (openBtn) openBtn.focus({ preventScroll: true });
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
    }

    if (openBtn) openBtn.addEventListener('click', open);
    root.querySelectorAll('[data-cc-sc-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    root.querySelectorAll('[data-cc-sc-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var name = tab.getAttribute('data-cc-sc-tab');
        root.querySelectorAll('[data-cc-sc-tab]').forEach(function (t) {
          var active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        root.querySelectorAll('[data-cc-sc-pane]').forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-cc-sc-pane') === name);
        });
      });
    });

    root.querySelectorAll('[data-cc-sc-unit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        currentUnit = btn.getAttribute('data-cc-sc-unit');
        root.querySelectorAll('[data-cc-sc-unit]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        renderTable(root, data, currentUnit, recommendedLabel);
      });
    });

    var calcBtn = root.querySelector('[data-cc-sc-calc]');

    if (!fitDataAvailable) {
      if (calcBtn) {
        calcBtn.disabled = true;
        calcBtn.style.opacity = '0.4';
        calcBtn.style.cursor = 'not-allowed';
      }
      if (resultEl) {
        resultEl.hidden = false;
        resultEl.innerHTML = '<p class="cc-sizechart__result-note">Sizing help for this piece isn’t set up yet — check the Size Chart tab for the sizes we offer, or message us and we’ll help you pick.</p>';
      }
      return;
    }

    if (calcBtn) {
      calcBtn.addEventListener('click', function () {
        if (!resultEl) return;
        resultEl.hidden = false;

        var hEl = root.querySelector('[data-cc-sc-height]');
        var wEl = root.querySelector('[data-cc-sc-weight]');
        var hStr = hEl ? String(hEl.value).trim() : '';
        var wStr = wEl ? String(wEl.value).trim() : '';
        var usualIdx = getUsualIndex(root);
        var fitPref = fitEl ? (parseInt(fitEl.value, 10) || 0) : 1;

        var h = null, w = null, bodyBest = null, bodyIdx = null;
        var hasBodyInput = hStr !== '' || wStr !== '';

        if (hasBodyInput) {
          h = parseFloat(hStr);
          w = parseFloat(wStr);
          if (!isFinite(h) || !isFinite(w) || h < 120 || h > 220 || w < 30 || w > 180) {
            resultEl.classList.add('is-error');
            resultEl.innerHTML = '<p class="cc-sizechart__result-note">Please enter both a height (120–220 cm) and a weight (30–180 kg) — or clear them and just pick your usual size.</p>';
            return;
          }
          bodyBest = recommendBody(data, h, w);
          bodyIdx = bodyBest ? bodyBest.index : null;
        }

        if (bodyIdx === null && usualIdx === null) {
          resultEl.classList.add('is-error');
          resultEl.innerHTML = '<p class="cc-sizechart__result-note">Enter your height and weight, or tap your usual size, so we have something to work with.</p>';
          return;
        }

        resultEl.classList.remove('is-error');

        var baseIdx;
        if (bodyIdx === null) {
          baseIdx = usualIdx;
        } else if (usualIdx === null) {
          baseIdx = bodyIdx;
        } else {
          baseIdx = Math.floor((bodyIdx + usualIdx) / 2);
        }

        var finalIdx = baseIdx;
        var maxIdx = data.sizes.length - 1;
        var fitNote = '';
        if (fitPref === 2) {
          if (baseIdx < maxIdx) {
            finalIdx = baseIdx + 1;
            fitNote = 'Sized up one for the full oversized silhouette.';
          } else {
            fitNote = 'Already the largest size we make in this piece — it will wear fully oversized on you.';
          }
        } else if (fitPref === 0) {
          if (baseIdx > 0 && canWear(data.sizes[baseIdx - 1], h, w)) {
            finalIdx = baseIdx - 1;
            fitNote = 'Went one size down for a cleaner, closer fit.';
          } else {
            fitNote = 'This is your cleanest fit — going smaller would be tight.';
          }
        } else {
          if (bodyBest && bodyBest.score === 0) {
            fitNote = 'Dialed in — right in your range for the intended fit.';
          } else {
            fitNote = 'Best balanced match for the intended fit.';
          }
        }

        var notes = [fitNote];
        if (bodyIdx !== null && usualIdx !== null && bodyIdx !== usualIdx) {
          notes.push('Your usual size and your measurements pointed to different sizes — we balanced both.');
        }
        if (data.estimated) {
          notes.push('Based on standard CC sizing — exact measurements for this piece are coming soon.');
        }

        var s = data.sizes[finalIdx];
        recommendedLabel = s.label;

        var specs = '';
        if (Array.isArray(s.measurements) && data.columns.length) {
          var parts = [];
          data.columns.forEach(function (c, i) {
            var val = s.measurements[i];
            if (val !== undefined && val !== null && val !== '') {
              parts.push(escapeHtml(c) + ' ' + escapeHtml(formatMeasure(val, data.unit, currentUnit)) + ' ' + currentUnit);
            }
          });
          specs = parts.join(' · ');
        }

        resultEl.innerHTML =
          '<p class="cc-sizechart__result-label">Recommended size</p>' +
          '<p class="cc-sizechart__result-size"><em>' + escapeHtml(s.label || '') + '</em></p>' +
          '<p class="cc-sizechart__result-note">' + notes.join(' ') + '</p>' +
          (specs ? '<p class="cc-sizechart__result-specs">' + specs + '</p>' : '');

        renderTable(root, data, currentUnit, recommendedLabel);
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-cc-sizechart]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
